import { IPortalStore } from '@mcn-platform/models';
import {
  ConversationStore,
  IConversationStore,
  Tokens,
  getDefaultClientAIContentUrl,
  getDefaultClientAIStreamUrl,
} from '@mcn-platform/converse-lib';

/**
 * Adapter to make a PortalStore conform to the IConversationStore interface.
 */
export default class ConversationStoreAdapter implements IConversationStore {
  private portalStore: IPortalStore;

  private conversationStore: IConversationStore;

  constructor(portalStore: IPortalStore) {
    this.portalStore = portalStore;
    this.conversationStore = ConversationStore.create({
      portalStore,
    });
  }

  get currentPortalID(): string {
    return this.portalStore.currentPortal?.id ?? '';
  }

  get currentOrganizationID(): string {
    return this.portalStore.currentOrganization?.id ?? '';
  }

  get amplifyEnvironment(): string {
    return this.portalStore.amplifyEnvironment;
  }

  get clientAIUrl(): string {
    return getDefaultClientAIStreamUrl(this.portalStore.amplifyEnvironment);
  }

  get clientAIContentUrl(): string {
    const org = this.portalStore.currentOrganization as { clientAIContentUrl?: string | null } | undefined;
    if (org?.clientAIContentUrl) {
      return org.clientAIContentUrl;
    }
    return getDefaultClientAIContentUrl(this.portalStore.amplifyEnvironment);
  }

  get clientAIBaseUrl(): string {
    return this.portalStore.currentPortal?.clientAIBaseUrl ?? '';
  }

  getOrLoad(type: string, target: string): any {
    return this.portalStore.getOrLoad(type, target);
  }

  createConversation(snapshot: any): any {
    console.log('createConversation', snapshot);
    return this.conversationStore.createConversation(snapshot);
  }

  setCurrentConversation(newConversation: unknown): unknown {
    return this.conversationStore.setCurrentConversation(newConversation);
  }

  get currentConversation(): any {
    return this.conversationStore.currentConversation;
  }

  get stylesheet(): any {
    return this.portalStore.currentPortal?.stylesheet;
  }

  get promptPlaceholder(): string {
    // This may be on portalStore or currentPortal
    // TODO: remove this MCN-2144 (move to tokens)
    return this.portalStore.currentPortal?.clientAIPromptPlaceholder ?? '';
  }

  get clientAIFeedbackButtons(): boolean {
    return this.portalStore.currentPortal?.clientAIFeedbackButtons ?? false;
  }

  get isAIServiceUnavailable(): boolean {
    return this.portalStore.currentOrganization?.isAIServiceUnavailable ?? false;
  }

  get clientAIConfig(): any {
    return this.portalStore.currentPortal?.clientAIConfig;
  }

  get clientAISuggestedPrompts(): string[] {
    // TODO: remove this MCN-2144 (move to tokens)
    return this.portalStore.currentPortal?.clientAISuggestedPrompts ?? [];
  }

  get clientAISuggestedPromptsLocalized(): boolean {
    return this.portalStore.currentPortal?.clientAIConfig?.clientAISuggestedPromptsLocalized 
      ?? this.portalStore.currentPortal?.clientAISuggestedPromptsLocalized 
      ?? false;
  }

  get clientAISuggestedPromptsL10N(): Record<string, string> | undefined {
    return this.portalStore.currentPortal?.clientAIConfig?.clientAISuggestedPromptsL10N 
      ?? this.portalStore.currentPortal?.clientAISuggestedPromptsL10N;
  }

  get clientAIStartingMessage(): string {
    // TODO: remove this MCN-2144 (move to tokens)
    return this.portalStore.currentPortal?.clientAIStartingMessage ?? '';
  }

  get clientAIPersistentPrompts(): string {
    // TODO: remove this MCN-2144 (move to tokens)
    return this.portalStore.currentPortal?.clientAIPersistentPrompts ?? '';
  }

  get clientAIPersistentPromptsLocalized(): boolean {
    return this.portalStore.currentPortal?.clientAIConfig?.clientAIPersistentPromptsLocalized 
      ?? this.portalStore.currentPortal?.clientAIPersistentPromptsLocalized 
      ?? false;
  }

  get clientAIPersistentPromptsL10N(): Record<string, string> | undefined {
    return this.portalStore.currentPortal?.clientAIConfig?.clientAIPersistentPromptsL10N 
      ?? this.portalStore.currentPortal?.clientAIPersistentPromptsL10N;
  }

  appendCustomerContext(data: any): void {
    this.conversationStore.appendCustomerContext(data);
  }

  get customerContext(): any {
    return this.portalStore.customerContext;
  }

  get currentUser(): any {
    return this.portalStore.currentUser;
  }

  get widgetProps(): any {
    return this.portalStore.currentPortal?.widgetProps ?? {};
  }

  get suggestedPrompts(): string[] {
    return this.conversationStore.suggestedPrompts ?? [];
  }

  get smartSummary(): { header: string; bullets: Array<{ title: string; body?: string }>; generatedByAiLabel?: string } | null {
    return this.conversationStore.smartSummary ?? null;
  }

  get suggestedPromptsHeader(): string {
    const portal = this.portalStore.currentPortal;
    return this.tokens.resolvePortalText(
      portal?.clientAIConfig?.pageSuggestedPromptsHeader,
      'pageSuggestedPromptsHeader',
    ) || this.tokens.resolvePortalText(
      null,
      'productPromptHeader',
    );
  }

  get isLoadingSuggestedPrompts(): boolean {
    return this.conversationStore.isLoadingSuggestedPrompts ?? false;
  }

  get lastSuggestedPromptsKey(): string {
    return this.conversationStore.lastSuggestedPromptsKey ?? '';
  }

  requestPageSpecificPrompts(location: string): Promise<void> {
    return this.conversationStore.requestPageSpecificPrompts(location);
  }

  requestPageAdditionalData(location: string): Promise<void> {
    return this.conversationStore.requestPageAdditionalData(location);
  }

  setSuggestedPrompts(prompts: string[]): void {
    this.conversationStore.setSuggestedPrompts(prompts);
  }

  setSmartSummary(summary: { header: string; bullets: Array<{ title: string; body?: string }>; generatedByAiLabel?: string } | null): void {
    this.conversationStore.setSmartSummary(summary);
  }

  get tokens(): Tokens {
    const tokens = new Tokens(this.portalStore.currentPortal);
    const locale = this.portalStore.customerContext?.currentPage?.locale
      || this.portalStore.customerContext?.locale;
    if (locale) {
      tokens.setLocale(locale);
    }
    return tokens;
  }

  get defaultLocale(): string {
    return this.portalStore.currentPortal?.defaultLocale ?? 'en';
  }
}
